import { useEffect, useRef, useState } from 'react'
import { Sparkles } from 'lucide-react'
import { Button, FormField, FormSection, IconButton, Tooltip } from '../common'
import { ActivityIndicator } from '../common/feedback/ActivityIndicator'
import { AssistBar } from '../patterns/AssistBar'
import { AI_ANALYSIS_DISCLAIMER } from './composerShared'
import styles from './PostComposer.module.css'

const CAPTION_ASSIST_ACTIONS = [
  { key: 'improve', label: 'Improve', instruction: 'Improve clarity and flow while preserving the facts and intent.' },
  { key: 'shorten', label: 'Shorten', instruction: 'Make this more concise without removing important facts.' },
]

const CAPTION_ASSIST_OVERFLOW_ACTIONS = [
  { key: 'conversational', label: 'More conversational', instruction: 'Use a more natural, conversational tone.' },
  { key: 'neutral', label: 'More neutral', instruction: 'Use a neutral, less promotional tone.' },
  { key: 'grammar', label: 'Fix grammar', instruction: 'Correct grammar and punctuation without changing the meaning.' },
]

export default function ContentSection({
  text,
  onTextChange,
  title = '',
  subtitle = '',
  onTitleChange = null,
  onSubtitleChange = null,
  creatorSlot = null,
  tagsSlot = null,
  tagsSuggestionsSlot = null,
  labelSlot = null,
  captionAction = null,
  captionTools = null,
  tagsAction = null,
  titleAction = null,
  collapseMoreOptions = false,
  moreOptionsAttention = false,
  // Opens "More content options" whenever it changes to a truthy value, e.g.
  // when the operator's Suggest tags request returns chips that live inside it.
  moreOptionsRevealKey = null,
  // Shown under the folded "More content options" heading, e.g. when tag
  // suggestions wait inside it, without opening it.
  moreOptionsNote = '',
  showSectionTitle = true,
}) {
  const shouldExposeMoreContent = Boolean(
    String(title || '').trim()
    || String(subtitle || '').trim()
    || moreOptionsAttention
  )
  const [moreContentOpen, setMoreContentOpen] = useState(shouldExposeMoreContent)
  const [captionAssistantOpen, setCaptionAssistantOpen] = useState(false)
  const [textSelection, setTextSelection] = useState(null)

  useEffect(() => {
    if (shouldExposeMoreContent) setMoreContentOpen(true)
  }, [shouldExposeMoreContent])

  // Only a key that changes while mounted opens the section: a remount (a
  // Guided step change) with the key it already had must not reopen it.
  const consumedRevealKeyRef = useRef(moreOptionsRevealKey)
  useEffect(() => {
    if (moreOptionsRevealKey === consumedRevealKeyRef.current) return
    consumedRevealKeyRef.current = moreOptionsRevealKey
    if (moreOptionsRevealKey) setMoreContentOpen(true)
  }, [moreOptionsRevealKey])

  useEffect(() => {
    if (captionAction?.pending || captionTools?.transcriptionPending || captionTools?.suggestions?.length) {
      setCaptionAssistantOpen(true)
    }
  }, [captionAction?.pending, captionTools?.suggestions?.length, captionTools?.transcriptionPending])

  useEffect(() => {
    if (!captionTools) setCaptionAssistantOpen(false)
  }, [captionTools])

  useEffect(() => {
    setTextSelection(null)
  }, [text])

  const requestCaptionAssistance = (instruction) => {
    if (!captionAction) return
    const selectionDirection = textSelection?.text
      ? `${instruction} Preserve all unselected copy. Only revise this selected passage: “${textSelection.text}”.`
      : instruction
    captionAction.onClick(selectionDirection)
  }

  const titleFields = (
    <>
      {onTitleChange ? (
        <div className={styles.titleFieldContainer}>
          <FormField
            label="Card title"
            name="title"
            value={title}
            onChange={(event) => onTitleChange(event.target.value)}
            maxLength={120}
            placeholder="e.g., Yankees three-leg power parlay"
          />
          {titleAction ? (
            <div className={styles.fieldActionRow}>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={titleAction.onClick}
                disabled={titleAction.disabled || titleAction.pending}
              >
                {titleAction.pending ? 'Generating…' : 'Generate new title'}
              </Button>
              {titleAction.reason ? <span className={styles.fieldActionHint}>{titleAction.reason}</span> : null}
              {titleAction.error ? <span className={styles.fieldActionError} role="alert">{titleAction.error}</span> : null}
            </div>
          ) : null}
        </div>
      ) : null}
      {onSubtitleChange ? (
        <FormField
          label="Card subtitle"
          name="subtitle"
          value={subtitle}
          onChange={(event) => onSubtitleChange(event.target.value)}
          maxLength={240}
          placeholder="e.g., Judge 2+ bases, Soto home run, Yankees to win"
        />
      ) : null}
    </>
  )

  const tagFields = (
    <>
      {tagsSlot}
      {tagsAction ? (
        <div className={styles.fieldActionRow}>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={tagsAction.onClick}
            disabled={tagsAction.disabled}
          >
            {tagsAction.pending ? 'Thinking...' : 'Suggest tags'}
          </Button>
          {tagsAction.reason ? <span className={styles.fieldActionHint}>{tagsAction.reason}</span> : null}
        </div>
      ) : null}
      {tagsSuggestionsSlot}
    </>
  )

  const captionAssistantToggle = captionTools ? (
    <Tooltip content={captionAssistantOpen ? 'Close caption assistant' : 'Suggest caption with AI'} placement="left">
      <IconButton
        label={captionAssistantOpen ? 'Close caption assistant' : 'Suggest caption with AI'}
        variant="ghost"
        className={styles.captionAssistantToggle}
        aria-controls="post-caption-assistant"
        aria-expanded={captionAssistantOpen}
        onClick={() => setCaptionAssistantOpen((current) => !current)}
      >
        <Sparkles size={15} aria-hidden="true" />
      </IconButton>
    </Tooltip>
  ) : null

  const postTextField = (
    <FormField
      label="Post text"
      labelExtra={captionAssistantToggle}
      name="text"
      type="textarea"
      value={text}
      onChange={(event) => onTextChange(event.target.value)}
      onSelect={(event) => {
        const start = event.currentTarget.selectionStart
        const end = event.currentTarget.selectionEnd
        const selectedText = start !== end ? event.currentTarget.value.slice(start, end).trim() : ''
        setTextSelection(selectedText ? { start, end, text: selectedText } : null)
        if (selectedText && captionTools) setCaptionAssistantOpen(true)
      }}
      rows={4}
      placeholder="Write the caption shown with this post."
    />
  )

  let captionAssistant = null
  if (captionTools && captionAssistantOpen) {
    captionAssistant = (
      <>
        <AssistBar
          id="post-caption-assistant"
          className={styles.captionAssistBar}
          label="Caption assistant"
          scope={textSelection?.text ? `${textSelection.text.length} characters selected` : 'Post text'}
          prompt={captionTools.guidance}
          promptLabel="Describe caption edits"
          placeholder="Describe edits"
          onPromptChange={captionTools.onGuidanceChange}
          onRequest={requestCaptionAssistance}
          actions={CAPTION_ASSIST_ACTIONS}
          overflowActions={CAPTION_ASSIST_OVERFLOW_ACTIONS}
          pending={Boolean(captionAction?.pending)}
          pendingLabel="Generating caption suggestions"
          disabled={Boolean(captionAction?.disabled && !captionAction?.pending)}
          results={captionTools.suggestions || []}
          resultsLabel="Caption suggestions"
          onApplyResult={captionTools.onUseSuggestion}
          note={AI_ANALYSIS_DISCLAIMER}
        />
        {/* Earlier suggestions keep the assistant open; when AI is blocked
            since, its controls are disabled, so say why here too. */}
        {captionAction?.reason ? (
          <div className={styles.fieldActionRow}>
            <span className={styles.fieldActionHint}>{captionAction.reason}</span>
          </div>
        ) : null}
        {captionTools.transcriptionPending ? (
          <ActivityIndicator compact status="processing" label="Analyzing video for caption ideas" />
        ) : null}
      </>
    )
  } else if (!captionTools && captionAction) {
    captionAssistant = (
      <>
        <div className={styles.fieldActionRow}>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => captionAction.onClick('Improve clarity and flow while preserving the facts and intent.')}
            disabled={captionAction.disabled}
          >
            {captionAction.pending ? 'Suggesting…' : 'Suggest caption'}
          </Button>
          {captionAction.reason ? <span className={styles.fieldActionHint}>{captionAction.reason}</span> : null}
        </div>
      </>
    )
  }

  return (
    <FormSection
      title={showSectionTitle ? 'Content' : null}
      headingSize={showSectionTitle ? 'subsection' : undefined}
      contentClassName={styles.sectionStack}
      className={!showSectionTitle ? styles.embeddedContentSection : undefined}
    >
      {collapseMoreOptions ? (
        <div className={styles.primaryContentStack}>
          {creatorSlot}
          {labelSlot}
          {postTextField}
          {captionAssistant}
        </div>
      ) : (
        <>
          {creatorSlot}
          {labelSlot}
          {titleFields}
          {postTextField}
          {captionAssistant}
        </>
      )}
      {collapseMoreOptions ? (
        <FormSection
          title="More content options"
          description={!moreContentOpen && moreOptionsNote ? moreOptionsNote : undefined}
          headingSize="subsection"
          collapsible
          expanded={moreContentOpen}
          onToggle={setMoreContentOpen}
          contentClassName={styles.sectionStack}
          className={styles.compactDisclosure}
        >
          {titleFields}
          {tagFields}
        </FormSection>
      ) : (
        tagFields
      )}
    </FormSection>
  )
}
